import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getCurrentIdToken, logout as cognitoLogout } from './auth';
import { decodeIdToken, type DecodedClaims } from './jwt';
import { rolesFromClaims, type Role } from './roles';

export type { Role } from './roles';

interface AuthState {
  loading: boolean;
  idToken?: string;
  claims?: DecodedClaims;
  roles: Role[];
  hasRole: (role: Role) => boolean;
  /** FR-3: angemeldet, aber ohne Eintrag in der Benutzertabelle -> keine Rolle, kein Zugriff. */
  hasAccess: boolean;
  /** Nur gesetzt, wenn Vor- und Nachname beide vorhanden sind. */
  fullName?: string;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

function composeFullName(claims: DecodedClaims | undefined): string | undefined {
  const given = claims?.givenName?.trim();
  const family = claims?.familyName?.trim();
  if (!given || !family) return undefined;
  return `${given} ${family}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string>();
  const [claims, setClaims] = useState<DecodedClaims>();

  const refresh = useCallback(async () => {
    const token = await getCurrentIdToken();
    setIdToken(token);
    setClaims(token ? decodeIdToken(token) : undefined);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Another tab signing out (or in) writes to localStorage; react to it so a
  // stale, already-signed-out session never keeps showing protected content.
  useEffect(() => {
    const onStorage = () => void refresh();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  const logout = useCallback(() => {
    cognitoLogout();
    setIdToken(undefined);
    setClaims(undefined);
  }, []);

  const roles = rolesFromClaims(claims);

  return (
    <AuthContext.Provider
      value={{
        loading,
        idToken,
        claims,
        roles,
        hasRole: (role: Role) => roles.includes(role),
        hasAccess: roles.length > 0,
        fullName: composeFullName(claims),
        refresh,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
